import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Zap, Target, Users, MapPin, Award } from 'lucide-react';

const About = () => {
  const { t } = useTranslation();

  const getValues = () => {
    return [
      {
        icon: Zap,
        titleKey: 'about.values.innovation',
        descKey: 'about.innovationDesc',
        gradient: 'from-yellow-500 to-orange-500'
      },
      {
        icon: Award,
        titleKey: 'about.values.excellence',
        descKey: 'about.excellenceDesc',
        gradient: 'from-purple-500 to-pink-500'
      },
      {
        icon: Shield,
        titleKey: 'about.values.security',
        descKey: 'about.securityDesc',
        gradient: 'from-green-500 to-teal-500'
      }
    ];
  };

  const getStats = () => {    
    return [
      { 
        number: '5+', 
        labelKey: 'about.stats.experience', 
        icon: Target 
      },
      { 
        number: '100+', 
        labelKey: 'about.stats.projects', 
        icon: Award 
      },
      { 
        number: '50+', 
        labelKey: 'about.stats.clients', 
        icon: Users 
      },
      { 
        number: '24/7', 
        labelKey: 'about.stats.support', 
        icon: Shield 
      }
    ];
  };

  return (
    <section id="about" className="section-spacing relative">
      <div className="absolute inset-0 bg-gradient-to-r from-secondary-900/10 via-transparent to-primary-900/10"></div>
      
      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-gradient">{t('about.header')}</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('about.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Content */}
          <div>
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <MapPin className="w-6 h-6 text-secondary-400" />
                <span className="text-secondary-400 font-semibold">Dubai • Sharjah • UAE</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">
                {t('about.partner')}
              </h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                {t('about.description1')}
              </p>
              <p className="text-gray-300 leading-relaxed">
                {t('about.description2')}
              </p>
            </div>

            {/* Mission Statement */}
            <div className="bg-gradient-to-r from-secondary-500/10 to-primary-500/10 border border-secondary-500/20 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-3 text-gradient">{t('about.mission')}</h4>
              <p className="text-gray-300 text-sm leading-relaxed">
                {t('about.missionText')}
              </p>
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {getStats().map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div
                    key={index}
                    className="card-glass p-6 text-center group hover:scale-105 transition-transform duration-300"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <IconComponent className="w-8 h-8 text-secondary-400 mx-auto mb-3 group-hover:text-primary-400 transition-colors duration-300" />
                    <div className="text-2xl font-bold text-gradient mb-1">{stat.number}</div>
                    <div className="text-sm text-gray-400">{t(stat.labelKey)}</div>
                  </div>
                );
              })}
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary-500/10 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary-500/10 rounded-full blur-2xl animate-pulse animation-delay-400"></div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-12">
            <span className="text-gradient">{t('about.valuesHeader')}</span>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {getValues().map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={index}
                  className="text-center group"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-r ${value.gradient} p-5 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="w-full h-full text-white" />
                    </div>
                    <div className={`absolute inset-0 w-20 h-20 mx-auto rounded-full bg-gradient-to-r ${value.gradient} opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-300`}></div>
                  </div>
                  
                  <h4 className="text-xl font-semibold mb-4 text-white group-hover:text-gradient transition-colors duration-300">
                    {t(value.titleKey)}
                  </h4>
                  <p className="text-gray-300 leading-relaxed">
                    {t(value.descKey)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-dark-800/80 via-dark-700/40 to-dark-800/80 border border-white/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4 text-gradient">
              {t('about.team')}
            </h3>
            <p className="text-gray-300 mb-6 max-w-3xl mx-auto">
              {t('about.teamDescription')}
            </p>
            <div className="flex justify-center space-x-8 items-center">
              <div className="text-center">
                <div className="text-lg font-bold text-secondary-400">
                  {t('about.teamRoles.developers')}
                </div>
                <div className="text-sm text-gray-400">Full-Stack</div>
              </div>
              <div className="w-px h-12 bg-white/20"></div>
              <div className="text-center">
                <div className="text-lg font-bold text-primary-400">Designers</div>
                <div className="text-sm text-gray-400">UI/UX</div>
              </div>
              <div className="w-px h-12 bg-white/20"></div>
              <div className="text-center">
                <div className="text-lg font-bold text-secondary-400">
                  {t('about.teamRoles.consultants')}
                </div>
                <div className="text-sm text-gray-400">
                  IT Strategy
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;